import { CreateToastFnReturn } from '@chakra-ui/react';
import sentry from '../sentry';
import { extractErrorMessages } from '../hooks/useApiErrorMessages';
import CustomToast from '../common/CustomToast';
import { createElement } from 'react';

interface DashboardMutationOptions {
  action: string;
  orgId?: string;
  onSuccess: () => void | Promise<void>;
  /** Return true to suppress the error toast */
  onError?: (error: unknown) => void | boolean;
  toast: CreateToastFnReturn;
  successMessage?: string;
  errorTitle?: string;
}

export async function dashboardMutation(
  mutationFn: () => Promise<unknown>,
  options: DashboardMutationOptions
): Promise<boolean> {
  const {
    action,
    orgId,
    onSuccess,
    onError,
    toast,
    successMessage,
    errorTitle = 'Something went wrong',
  } = options;

  try {
    await mutationFn();
    if (successMessage) {
      toast({
        render: ({ onClose }) =>
          createElement(CustomToast, {
            status: 'success',
            title: successMessage,
            onClose,
          }),
      });
    }
    await onSuccess();
    return true;
  } catch (error) {
    const errorMessages = extractApiErrorMessages(error);
    const isNetworkError = isNetworkFailure(error);
    const httpStatus = extractHttpStatus(error);
    const failureClass = isNetworkError
      ? 'network_error'
      : httpStatus
        ? 'api_error'
        : 'client_error';

    const title = isNetworkError
      ? 'Network error — check your connection and try again'
      : errorTitle;

    const description = isNetworkError
      ? undefined
      : errorMessages.length === 1
        ? errorMessages[0]
        : errorMessages.length > 1
          ? errorMessages
          : 'Please try again';

    const suppressToast = onError?.(error) === true;

    if (!suppressToast) {
      toast({
        render: ({ onClose }) =>
          createElement(CustomToast, {
            status: 'error',
            title,
            description,
            onClose,
          }),
      });
    }

    sentry.sendError(error, {
      tags: {
        dashboard_action: action,
        failure_class: failureClass,
        ...(httpStatus != null && { http_status: String(httpStatus) }),
      },
      extra: {
        action,
        orgId,
        errorMessages,
        httpStatus,
      },
    });

    return false;
  }
}

function extractApiErrorMessages(error: unknown): string[] {
  const data = (error as { data?: Record<string, unknown> })?.data;
  if (data) return extractErrorMessages(data);

  // Raw axios errors
  const responseData = (
    error as { response?: { data?: Record<string, unknown> } }
  )?.response?.data;
  if (responseData) return extractErrorMessages(responseData);

  if (error instanceof Error) return [error.message];
  return [];
}

function extractHttpStatus(error: unknown): number | undefined {
  return (
    (error as { status?: number })?.status ??
    (error as { response?: { status?: number } })?.response?.status ??
    undefined
  );
}

function isNetworkFailure(error: unknown): boolean {
  if (error instanceof TypeError && error.message === 'Failed to fetch')
    return true;
  const code = (error as { code?: string })?.code;
  if (code === 'ERR_NETWORK' || code === 'ECONNABORTED') return true;

  // RTK Query via axiosBaseQuery: status is undefined and data is the error message string
  const rtk = error as { status?: number; data?: unknown };
  if (rtk.status === undefined && typeof rtk.data === 'string' && rtk.data.includes('Network Error'))
    return true;

  return false;
}
